import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { HaiSelect } from '@/components/HaiSelect';
import { LightboxShell } from '@/components/LightboxShell';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { useToast } from '@/components/Toast';
import {
  CoverControls,
  CoverPreview,
  useCoverPicker,
  type CoverDraft,
} from '@/features/covers/CoverPicker';
import { mapCoverErrorMessage } from '@/features/covers/coverErrorMap';
import { prepareCoverUpload } from '@/lib/coverImageFile';
import { services } from '@/services';
import type { BrandKit } from '@/types';

/**
 * ProjectCreateDialog — 建立新專案(R293;對齊 D3 `projects.js` `formHTML()` 的
 * `create` 模式,容器 = 共用燈箱外框 {@link LightboxShell} 的 `narrow` 尺寸 ——
 * `min(560px, 100vw-48px)` / `max-height: 88vh`,與 D3 `.pj-modal` 的
 * `min(560px,92vw)` / `88vh` 同一組數字)
 * -----------------------------------------------------------------------------
 * 版型(由上而下,D3 `.pj-fbody` 逐欄對齊):
 *
 *   ┌───────────────────────────────────────────┐
 *   │ 建立新專案                             ✕ │  ← .pj-fhead
 *   ├───────────────────────────────────────────┤
 *   │ ┌───────────────────────────────────────┐ │
 *   │ │  封面預覽(整塊可點 = 上傳)          │ │  ← .pj-prev(104px)
 *   │ └───────────────────────────────────────┘ │
 *   │ 想叫這個專案什麼名字？ 必填               │
 *   │ 想補充什麼說明？       選填  (n / 120)    │
 *   │ 要引用哪一份品牌規範？ 選填               │
 *   │ 想用什麼當封面？       選填  (色票 + 上傳)│
 *   ├───────────────────────────────────────────┤
 *   │                      [取消] [建立專案]    │  ← .pj-ffoot
 *   └───────────────────────────────────────────┘
 *
 * **與 D3 的三個刻意差異**(D3 是 UI/UX 參考,行為由 owner 拍板):
 *   1. **沒有「要放在哪個工作區」欄**(owner 2026-08-30:「沒有跨工作區這東西」)。
 *      BE 那側也一致:`group_id` IMMUTABLE、沒有搬移端點,專案一律建在
 *      `X-Current-Group` 指的工作區 —— 畫一顆選了也送不出去的下拉只會製造誤解。
 *   2. **沒有右側「建立完成後 A/B/C/D」導覽欄**(owner 同批要求移除)。那是舊
 *      `/new` 整頁版的產物,D3 的建立表單從來就只有燈箱本體。
 *   3. **名稱上限 100 而不是 D3 的 40** —— 100 是 BE 契約(`ProjectCreateRequest`),
 *      也是「編輯專案資訊」dialog 的值;抄 D3 的 40 會讓建立時打不完的名字在編輯時
 *      又打得下,兩個表單各一套上限。說明上限則跟著 D3 = 120(與編輯 dialog 同值)。
 *
 * **封面兩步流程**(BE 契約決定,不是這層能解掉的):`POST /projects/{pid}/cover`
 * 需要 pid → create 成功之後才傳圖;**那一步失敗絕對不回滾**(專案已建好,為了封面
 * 把它刪掉是把小失敗換成大失敗),處置 = 照常進畫布 + toast。
 */

/** BE `ProjectCreateRequest.name` 上限。 */
const NAME_MAX_CHARS = 100;
/** 說明上限 —— D3 `maxlength="120"`,與「編輯專案資訊」dialog 同值。 */
const DESC_MAX_CHARS = 120;

export function ProjectCreateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('projectCreate');
  // 封面的文案掛在 common(projects / brand_kits 共用同一組錯誤碼與色票名)
  const { t: tCommon } = useTranslation('common');
  const { show: showToast } = useToast();
  const navigate = useNavigate();

  // ─── 載入既有 brand kit list(給品牌規範下拉用)────────────────────────────
  // R397 — 品牌規範 UI 隱藏(brandKitsGate;owner 授權才開):gate 關不抓清單、下面的欄位不渲染。
  const [kits, setKits] = useState<BrandKit[]>([]);
  const [kitsLoadError, setKitsLoadError] = useState(false);
  useEffect(() => {
    if (!BRAND_KITS_UI_VISIBLE) return;
    let cancelled = false;
    services.brandKitsService
      .list()
      .then((list) => {
        if (!cancelled) setKits(list);
      })
      .catch(() => {
        // 載入失敗仍開放建專案(等同「不引用」);只把下拉停用並說明。
        if (!cancelled) setKitsLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Form state ─────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** `''` = 不引用(D3 下拉的第一個選項)。 */
  const [brandKitId, setBrandKitId] = useState('');
  /**
   * R245 封面。**預設刻意是 `none` 而不是 D3 的第一顆色票** —— 預設色票等於把
   * 現有的「品牌色漸層 fallback」對所有新專案換掉,那是反轉既有行為。D3 是 UI/UX
   * 參考,行為由 Bryan 拍板(要改成預設 pine 只需要改這一行的初值)。
   */
  const [cover, setCover] = useState<CoverDraft>({ kind: 'none' });
  const picker = useCoverPicker({ value: cover, onChange: setCover });

  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submitLockRef = useRef(false);

  // ⚠️ **不在這裡 focus 名稱欄**:`LightboxShell` mount 時會把焦點放到關閉鈕
  // (全站燈箱一致的 a11y 慣例),子元件的 effect 先跑、會被它蓋掉 —— 寫了只是
  // 讓人以為有效。D3 那句 `setTimeout(focus,60)` 不搬。
  const nameRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (): Promise<void> => {
    if (submitLockRef.current) return;

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setValidationError(t('dialog.errors.nameRequired'));
      nameRef.current?.focus();
      return;
    }
    if (trimmedName.length > NAME_MAX_CHARS) {
      setValidationError(t('dialog.errors.nameTooLong'));
      return;
    }
    setValidationError(null);
    setError(null);

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const trimmedDesc = description.trim();
      const project = await services.projectsService.create({
        name: trimmedName,
        ...(trimmedDesc.length > 0 ? { description: trimmedDesc } : {}),
        ...(brandKitId.length > 0 ? { brandKitId } : {}),
        // 純色可以直接帶進 create;圖片不行(要 pid),見下面第二步。
        // `none` 不帶 —— BE default_factory 就是 none,少送一個欄位。
        ...(cover.kind === 'color' ? { cover: { kind: 'color' as const, color: cover.color } } : {}),
      });

      // ── 第二步:圖片封面 ────────────────────────────────────────────────
      // ⚠️ 失敗不回滾(檔頭已說明);BE 那側保證封面失敗時 doc 一個位元組都沒動。
      // ToastProvider 掛在 router 之外(`app/providers.tsx`),所以 navigate 之後
      // toast 還在。
      if (cover.kind === 'image') {
        try {
          const prepared = await prepareCoverUpload(cover.file);
          await services.projectsService.setCoverImage(project.projectId, prepared);
        } catch (coverErr) {
          console.error('[ProjectCreateDialog] cover upload failed', coverErr);
          showToast({
            tone: 'error',
            durationMs: 8000,
            message: tCommon('cover.errors.uploadFailedKeepProject', {
              reason: mapCoverErrorMessage(coverErr, tCommon),
            }),
          });
        }
      }

      // R164 大改版:靈感來源頁(SourcesPage)已刪 — 建專案完成直進工作區畫布。
      // 不 seed 空 ROOT(R99 起維持;雙色卡制下綠卡由使用者雙擊建立)。
      navigate(`/canvas/${project.projectId}`);
    } catch (err) {
      console.error('[ProjectCreateDialog] create project failed', err);
      setError(t('dialog.errors.createFailed'));
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const closeIfIdle = (): void => {
    if (!submitting) onClose();
  };

  /**
   * 名稱欄按 Enter = 送出。
   *
   * 這裡刻意**沒有 `<form>`**:`LightboxShell` 把 head / body / footer 畫成三個
   * 兄弟節點,`<form>` 包不到 footer 的送出鈕(要靠 `form=` 屬性關聯,jsdom 支援度
   * 不穩)。同 `ProjectEditDialog` 慣例:按鈕走 onClick,Enter 走這一條。
   */
  const onNameKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <LightboxShell
      size="narrow"
      ariaLabel={t('dialog.title')}
      closeTitle={t('dialog.close')}
      onClose={closeIfIdle}
      data-testid="project-create-dialog"
      bodyStyle={bodyStyle}
      head={
        <h2 id="pc-title" style={titleStyle}>
          {t('dialog.title')}
        </h2>
      }
      footer={
        <div style={footRowStyle}>
          {/* CIS:取消 = 幽靈灰 `.btn-ghost`、確認 = 實心 teal `.btn-solid`(底 `--btn-solid`;
              主色 #0D9488 配白字只有 3.74:1)。停用態走 class,不再整顆 opacity 調淡。 */}
          <button
            type="button"
            className="hai-btn-ghost"
            onClick={closeIfIdle}
            disabled={submitting}
            data-testid="pc-cancel"
          >
            {t('dialog.cancel')}
          </button>
          <button
            type="button"
            className="hai-btn-solid"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            data-testid="pc-submit"
          >
            {submitting ? t('dialog.submitting') : t('dialog.submit')}
          </button>
        </div>
      }
    >
            {/* 封面預覽 —— D3 把它放在最上面當「這個專案長什麼樣」的即時回饋;
                色票列在最後一欄(同一個 controller,見 CoverPicker 檔頭)。 */}
            <CoverPreview picker={picker} previewLabel={name} height={104} />

            <Field
              htmlFor="pc-name"
              label={t('dialog.nameLabel')}
              flag={t('dialog.required')}
              flagTone="required"
              hint={t('dialog.nameHint')}
            >
              <input
                id="pc-name"
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onNameKeyDown}
                placeholder={t('dialog.namePlaceholder')}
                maxLength={NAME_MAX_CHARS}
                style={inputStyle}
                disabled={submitting}
                aria-invalid={validationError !== null}
                aria-describedby={validationError ? 'pc-name-error' : undefined}
                data-testid="pc-name-input"
              />
              {validationError && (
                <p id="pc-name-error" role="alert" style={errorTextStyle}>
                  {validationError}
                </p>
              )}
            </Field>

            <Field
              htmlFor="pc-description"
              label={t('dialog.descLabel')}
              flag={t('dialog.optional')}
              flagTone="optional"
              hint={t('dialog.descCount', { used: description.length, max: DESC_MAX_CHARS })}
            >
              <textarea
                id="pc-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, DESC_MAX_CHARS))}
                placeholder={t('dialog.descPlaceholder')}
                maxLength={DESC_MAX_CHARS}
                disabled={submitting}
                style={{ ...inputStyle, resize: 'none', lineHeight: 1.55 }}
                data-testid="pc-description-input"
              />
            </Field>

            {BRAND_KITS_UI_VISIBLE && (
            <Field
              htmlFor="pc-brand-kit"
              label={t('dialog.brandLabel')}
              flag={t('dialog.optional')}
              flagTone="optional"
              hint={kitsLoadError ? t('dialog.brandLoadError') : t('dialog.brandHint')}
            >
              {/* R393 — CIS 禁原生 `<select>` → HaiSelect(field = D3 `.pj-ddbtn`:左 shield
                  圖示 + 值 + cv)。選單 portal + fixed,燈箱 body 的 overflow 裁不到它。
                  `id` 讓上面的 `<label htmlFor>` 照樣指得到觸發鈕。 */}
              <HaiSelect
                id="pc-brand-kit"
                icon="shield"
                value={brandKitId}
                onChange={(v) => setBrandKitId(v)}
                disabled={submitting || kitsLoadError || kits.length === 0}
                aria-labelledby="pc-brand-kit-label"
                data-testid="pc-brand-kit-select"
                options={[
                  { value: '', label: t('dialog.brandNone') },
                  ...kits.map((k) => ({ value: k.brandKitId, label: k.name })),
                ]}
              />
            </Field>
            )}

            <Field
              label={t('dialog.coverLabel')}
              flag={t('dialog.optional')}
              flagTone="optional"
            >
              <CoverControls picker={picker} />
            </Field>

      {error && (
        <div role="alert" style={errorBannerStyle} data-testid="pc-error">
          {error}
        </div>
      )}
    </LightboxShell>
  );
}

// ─── Field(D3 .pj-f:label + 控件 + hint 的三段式)─────────────────────────

interface FieldProps {
  label: string;
  flag: string;
  flagTone: 'required' | 'optional';
  htmlFor?: string;
  hint?: string;
  children: ReactNode;
}

function Field({ label, flag, flagTone, htmlFor, hint, children }: FieldProps) {
  return (
    <div style={fieldStyle}>
      {/* 沒有 htmlFor 的欄位(封面是一組按鈕,不是單一控件)用 <div> —— `<label>`
          指不到任何控件時對讀屏是雜訊。 */}
      {/* `id` = `${htmlFor}-label`:HaiSelect 的選單(portal 在 body)靠 aria-labelledby 指回來 */}
      {htmlFor ? (
        <label htmlFor={htmlFor} id={`${htmlFor}-label`} style={labelStyle}>
          {label}
          <em style={flagTone === 'required' ? requiredPillStyle : optionalPillStyle}>{flag}</em>
        </label>
      ) : (
        <div style={labelStyle}>
          {label}
          <em style={flagTone === 'required' ? requiredPillStyle : optionalPillStyle}>{flag}</em>
        </div>
      )}
      {children}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );
}

// ─── styles(對齊 D3 `專案首頁.html` .pj-* 燈箱;色彩一律 token)──────────────






const titleStyle: CSSProperties = {
  margin: 0,
  flex: 1,
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--text-strong)',
};


/** D3 `.pj-fbody` —— 欄與欄之間 16px;外框 padding 由 `LightboxShell` 給。 */
const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

/** footer 兩顆鈕靠右(`LightboxShell` 的 foot 是 flex + gap 8)。 */
const footRowStyle: CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  gap: 8,
};

const fieldStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };

const labelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--ink-500)',
};

/** D3 `.pj-lbl em` — teal pill;`--concept-*` 是全站唯一會隨主題翻的 teal pill 對。 */
const requiredPillStyle: CSSProperties = {
  fontStyle: 'normal',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  background: 'var(--concept-bg)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 7px',
};

const optionalPillStyle: CSSProperties = {
  ...requiredPillStyle,
  color: 'var(--ink-500)',
  background: 'var(--ink-850)',
};

/**
 * ⚠️ **不寫 `outline: 'none'`**:全站焦點環(global.css `:where(...):focus-visible`,2px teal)
 * 是 specificity 0,inline 的 `outline: none` 會贏 → 名稱 / 說明欄按 Tab 進來看不到焦點(CIS 交互)。
 */
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--hairline)',
  background: 'var(--ink-900)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

/** CIS hint = 11 / 次文字(`--ink-600` 以下不可當文字色)。 */
const hintStyle: CSSProperties = { fontSize: 11, color: 'var(--ink-500)' };

/** 錯誤 = 玫瑰(CIS 不用 `--danger-*` / `--err` 表示錯誤)。 */
const errorTextStyle: CSSProperties = { margin: 0, fontSize: 11, color: 'var(--rose-700)' };

const errorBannerStyle: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 'var(--r-sm)',
  background: 'var(--rose-50)',
  border: '1px solid var(--rose-200)',
  fontSize: 12.5,
  color: 'var(--rose-700)',
};
